function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[character]));
}

function reportLineHtml(line) {
  const content = escapeHtml(line).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const heading = content.match(/^(#{1,4})\s+(.+)$/);
  if (heading) {
    const level = Math.min(4, heading[1].length + 1);
    return `<h${level}>${heading[2]}</h${level}>`;
  }
  if (/^[-•] /.test(content)) return `<p class="bullet">• ${content.slice(2)}</p>`;
  if (/^(?:⸻+|━+|---+)$/.test(content.trim())) return '<hr>';
  return content.trim() ? `<p>${content}</p>` : '';
}

export function buildReviewReportHtml({ result, title, brandName = '小影' }) {
  const meta = result.meta || result;
  const reportTitle = title || '剧本审核报告';
  return `<!doctype html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(reportTitle)} · 剧本审核报告</title>
  <style>
    * { box-sizing: border-box; }
    html { color-scheme: light; background: #fff; }
    body { max-width: 820px; margin: 0 auto; padding: 32px; color: #302932; background: #fff; font: 14px/1.8 -apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif; overflow-wrap: anywhere; }
    header { border-bottom: 1px solid #ccc; padding-bottom: 16px; margin-bottom: 24px; }
    h1 { margin: 4px 0 12px; font-size: 24px; line-height: 1.4; }
    h2, h3, h4 { margin: 22px 0 8px; line-height: 1.5; break-after: avoid; page-break-after: avoid; }
    h2 { font-size: 19px; } h3 { font-size: 17px; } h4 { font-size: 15px; }
    p { margin: 6px 0; white-space: pre-wrap; orphans: 3; widows: 3; }
    .meta { color: #615961; font-size: 12px; }
    .bullet { padding-left: 1em; text-indent: -1em; }
    hr { border: 0; border-top: 1px solid #ddd; margin: 16px 0; }
    button { padding: 7px 12px; border: 1px solid #ccc; border-radius: 8px; background: #fff; color: #302932; font: inherit; cursor: pointer; }
    button:focus-visible { outline: 2px solid #765f89; outline-offset: 3px; }
    .actions { margin-bottom: 24px; }
    footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 12px; color: #615961; }
    @media (max-width: 600px) { body { padding: 20px; } button { min-height: 44px; } }
    @page { size: A4; margin: 18mm; }
    @media print {
      html, body { width: auto; max-width: none; height: auto; max-height: none; overflow: visible; }
      body { margin: 0; padding: 0; font-size: 11pt; }
      .actions { display: none; }
      main { display: block; overflow: visible; }
    }
  </style>
</head>
<body>
  <div class="actions"><button type="button" id="print-report">打印 / 另存为 PDF</button></div>
  <header>
    <p class="meta">${escapeHtml(brandName)} · 剧本审核报告</p>
    <h1>${escapeHtml(reportTitle)}</h1>
    <p>评级：${escapeHtml(meta.rating || '—')}${meta.score != null && meta.score !== '' ? ` · ${escapeHtml(meta.score)} 分` : ''}</p>
    <p>结论：${escapeHtml(meta.conclusion || '—')}</p>
    ${meta.ai_percentage != null ? `<p class="meta">AI 含量：${escapeHtml(meta.ai_percentage)}%</p>` : ''}
  </header>
  <main>${result.report.split(/\r?\n/).map(reportLineHtml).join('\n')}</main>
  <footer>${escapeHtml(brandName)} · 剧本审核报告</footer>
</body>
</html>`;
}

export async function printReviewReport(options) {
  if (!options.result?.report?.trim()) throw new Error('这份报告还没有正文，暂时无法导出。');
  // Open synchronously from the click so browsers do not treat it as an unsolicited popup.
  const reportWindow = window.open('', '_blank');
  if (!reportWindow) throw new Error('报告窗口被浏览器拦截，请允许弹出窗口后重试。');

  try {
    reportWindow.opener = null;
    reportWindow.document.open();
    reportWindow.document.write(buildReviewReportHtml(options));
    reportWindow.document.close();
    const print = () => {
      reportWindow.focus();
      reportWindow.print();
    };
    reportWindow.document.getElementById('print-report').addEventListener('click', print);
    await reportWindow.document.fonts?.ready;
    if (reportWindow.closed) throw new Error('报告窗口已关闭，请重新导出。');
    print();
  } catch (error) {
    if (!reportWindow.closed) reportWindow.close();
    throw error;
  }
}
